import { Box, Button, Card, CardContent, CardHeader, Divider, FormLabel, Stack, IconButton } from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DownloadIcon from '@mui/icons-material/Download';
import { useState, useMemo } from 'react';
import JSONGrid from '@redheadphone/react-json-grid';
import { JsonViewer as JV, defineDataType, objectType, DataItemProps } from '@textea/json-viewer';
import { useElementSize } from '@custom-react-hooks/use-element-size';

type Props = { json: any; maxHeight: number; showLabel?: boolean; label?: string; error?: string };

const isDownloadableNode = (value: any) => {
  return value && typeof value === 'object' && 'content' in value && 'type' in value && 'mimeType' in value;
};

const downloadNode = (value: any) => {
  const { content, mimeType, type } = value;
  const link = document.createElement('a');

  if (type === 'PlainText') {
    const blob = new Blob([content], { type: mimeType });
    link.href = URL.createObjectURL(blob);
  } else {
    link.href = `data:${mimeType};base64,${content}`;
  }

  link.download = value.filename || value.name || `download.${mimeType.split('/')[1] || 'bin'}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (type === 'PlainText') {
    URL.revokeObjectURL(link.href);
  }
};

const downloadDataType = defineDataType({
  is: isDownloadableNode,
  Component: objectType.Component,
  PostComponent: ({ value }: DataItemProps<any>) => (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        downloadNode(value);
      }}
      sx={{ ml: 1, p: 0.5 }}
    >
      <DownloadIcon fontSize="inherit" />
    </IconButton>
  ),
});

const enrichJsonForGrid = (val: any): any => {
  if (isDownloadableNode(val)) {
    return { ...val, Download: '⬇️' };
  }
  if (Array.isArray(val)) {
    return val.map(enrichJsonForGrid);
  }
  if (val && typeof val === 'object') {
    const res: any = {};
    for (const key in val) {
      res[key] = enrichJsonForGrid(val[key]);
    }
    return res;
  }
  return val;
};

const JsonViewer = ({ json, showLabel = true, label, maxHeight, error }: Props) => {
  const [headerRef, headerSize] = useElementSize();

  const [gridMode, setGridMode] = useState(true);

  const enrichedJson = useMemo(() => enrichJsonForGrid(json), [json]);

  const handleGridSelect = (path: any[]) => {
    if (path[path.length - 1] === 'Download') {
      const nodePath = path.slice(0, -1);
      let node = json;
      for (const key of nodePath) {
        if (Array.isArray(key)) {
          // JSONGrid sometimes returns [index] for arrays
          node = node?.[key[0]];
        } else {
          node = node?.[key];
        }
      }
      if (isDownloadableNode(node)) {
        downloadNode(node);
      }
    }
  };

  return (
    <Card variant="outlined" sx={{ border: label || error ? 1 : 0, borderColor: 'divider', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
      {
        <CardHeader
          ref={headerRef}
          disableTypography
          title={
            showLabel && (label || error) ? (
              <FormLabel sx={{ fontSize: '12px', fontWeight: 600 }}>{label}</FormLabel>
            ) : (
              ''
            )
          }
          action={
            <Stack direction={'row'} justifyContent={'flex-end'} spacing={1}>
              <Button
                size="small"
                variant={!gridMode ? 'outlined' : 'text'}
                startIcon={<DataObjectIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => setGridMode(false)}
                sx={{ height: '24px', fontSize: '11px', py: 0, minWidth: 'auto' }}
              >
                Json view
              </Button>
              <Button
                size="small"
                variant={gridMode ? 'outlined' : 'text'}
                startIcon={<GridOnIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => setGridMode(true)}
                sx={{ height: '24px', fontSize: '11px', py: 0, minWidth: 'auto' }}
              >
                Grid view
              </Button>
            </Stack>
          }
          sx={{
            p: 0.75,
            minHeight: '36px',
            alignItems: 'center',
            '& .MuiCardHeader-action': {
              margin: 0,
              alignSelf: 'center',
            },
          }}
        />
      }
      {showLabel && (label || error) && <Divider />}
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <Box sx={{ maxWidth: '100%', width: '100%', overflowX: 'auto', maxHeight: maxHeight > 0 ? maxHeight - 2 * headerSize.height : 'none', boxSizing: 'border-box' }}>
          {gridMode && <JSONGrid data={enrichedJson} onSelect={handleGridSelect} theme="remedy" />}
          {!gridMode && (
            <JV
              value={json}
              rootName={false}
              displayDataTypes={false}
              collapseStringsAfterLength={20}
              valueTypes={[downloadDataType]}
              // sx={{ height: maxHeight / 2, overflowY: 'auto' }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default JsonViewer;
